import { Timestamp } from 'firebase/firestore';
import { addDays, format } from 'date-fns';
import type { VacationRequest, VacationSegment } from '@/features/Vacations/models/vacation.types';
import type { VacationDpRow } from '@/features/Vacations/models/vacationDpBoard.types';

const CLIENT_NAME = 'Cliente Exemplo';
const RESPONSIBLE = { responsibleId: 'demo-responsible', responsibleName: 'Responsável Exemplo' };

function firstMondayOnOrAfter(base: Date, offsetDays: number): Date {
    const date = addDays(base, offsetDays);
    return addDays(date, (8 - date.getDay()) % 7);
}

function buildSegment(order: number, start: Date, days: number): VacationSegment {
    return {
        order,
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(addDays(start, days - 1), 'yyyy-MM-dd'),
        days,
        approved: null,
    } as VacationSegment;
}

export function buildDpDecisionDemo(today: Date): {
    row: VacationDpRow;
    request: VacationRequest;
    allRows: VacationDpRow[];
} {
    const firstStart = firstMondayOnOrAfter(today, 20);
    const first = buildSegment(1, firstStart, 15);
    const second = buildSegment(2, addDays(firstStart, 30), 15);

    const request = {
        id: 'demo-dp-request',
        collaboratorId: 'demo-dp-ana',
        collaboratorName: 'Ana Exemplo',
        collaboratorEmail: 'ana.exemplo@rarolabs.com.br',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        requestedAt: Timestamp.now(),
        status: 'PendingDP',
        modality: '15+15',
        totalDays: 30,
        segments: [first, second],
        managerEmail: 'gestor@rarolabs.com.br',
        managerApproval: { status: 'Approved', by: 'gestor@rarolabs.com.br', at: Timestamp.now(), note: null },
        clientEmails: ['cliente@exemplo.com.br'],
        clientApproval: { status: 'Approved', by: 'cliente@exemplo.com.br', at: Timestamp.now() },
        sellRequest: { requested: false, days: 0, status: 'None' },
        signature: { sentAt: null, signedAt: null },
        dpApproval: { status: 'Pending', by: null, at: null },
        dpSegmentDecisions: null,
        conflictsSnapshot: [
            {
                allocationId: 'demo-allocation',
                clientName: CLIENT_NAME,
                projectName: 'Projeto Exemplo',
                startDate: first.startDate,
                endDate: first.endDate,
            },
        ],
        correction: null,
        rejectionReason: null,
        timeline: [],
    } as unknown as VacationRequest;

    const row = {
        collaboratorId: 'demo-dp-ana',
        collaboratorName: 'Ana Exemplo',
        collaboratorEmail: 'ana.exemplo@rarolabs.com.br',
        collaboratorRole: 'Desenvolvedora',
        managerName: 'Gestor Exemplo',
        balanceDays: 30,
        expiredDays: 0,
        pastLimitDays: 0,
        daysWithinDeadline: 30,
        scheduledDays: 30,
        pendingDays: 30,
        concessiveDeadline: null,
        legalDeadline: null,
        deadlineIsProjected: false,
        approvalDeadline: null,
        legacyTakenDays: 0,
        legacyScheduledDays: 0,
        monthsWorked: 24,
        unallocatedDays: 0,
        request,
        eligible: true,
        approvedPeriods: [],
        clientNames: [CLIENT_NAME],
        clientResponsibles: [{ clientName: CLIENT_NAME, ...RESPONSIBLE }],
    } as unknown as VacationDpRow;

    const colleague = {
        collaboratorId: 'demo-dp-bruno',
        collaboratorName: 'Bruno Exemplo',
        collaboratorEmail: 'bruno.exemplo@rarolabs.com.br',
        collaboratorRole: 'Analista',
        balanceDays: 30,
        expiredDays: 0,
        pastLimitDays: 0,
        daysWithinDeadline: 30,
        scheduledDays: 15,
        pendingDays: 0,
        concessiveDeadline: null,
        legalDeadline: null,
        deadlineIsProjected: false,
        approvalDeadline: null,
        legacyTakenDays: 0,
        legacyScheduledDays: 0,
        monthsWorked: 24,
        unallocatedDays: 0,
        request: null,
        eligible: true,
        approvedPeriods: [
            {
                requestId: 'demo-dp-colleague',
                status: 'Scheduled',
                startDate: second.startDate,
                endDate: second.endDate,
                days: 15,
                hasApprovedSell: false,
                segmentOrder: 1,
                segmentCount: 1,
                modality: '15+15',
            },
        ],
        clientNames: [CLIENT_NAME],
        clientResponsibles: [{ clientName: CLIENT_NAME, ...RESPONSIBLE }],
    } as unknown as VacationDpRow;

    return { row, request, allRows: [row, colleague] };
}
